import { useCallback, useEffect, useState } from 'react'
import { Coordinates, FetchStoresError, StoreWithDistance } from './types'
import { geocode } from '../services/google-maps-service'
import { mapSitesToStores } from '../utils/map-sites-to-stores'
import { getSites } from '../services/cinch-servicing'
import { datadogRum } from '@datadog/browser-rum'

const TWENTY_MILES_IN_METERS = 32_186

export const useFetchAllStoresAndCalculateDistance = (
  postcode?: string,
  under60Months?: boolean,
  vehicleMake?: string,
) => {
  const [storesWithDistance, setStoresWithDistance] = useState<
    StoreWithDistance[]
  >([])
  const [customerCoordinates, setCustomerCoordinates] = useState<
    Coordinates | undefined
  >()
  const [
    nearestMatchingStoreWithin20Miles,
    setNearestMatchingStoreWithin20Miles,
  ] = useState<StoreWithDistance>()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<FetchStoresError | null>(null)

  useEffect(() => {
    if (postcode) {
      fetchAllStoresAndCalculateDistances(postcode)
    }
  }, [postcode])

  const fetchAllStoresAndCalculateDistances = useCallback(
    async (postcode: string) => {
      setLoading(true)
      setError(null)
      setNearestMatchingStoreWithin20Miles(undefined)
      try {
        const customerCoordinates = await geocode(postcode)
        if (!customerCoordinates) {
          datadogRum.addError(new Error('Cinch Servicing: Postcode Error'), {
            cinchServicing: true,
            postcode,
          })
          setError('Postcode')
        } else {
          const sites = await getSites({
            latitude: customerCoordinates.lat,
            longitude: customerCoordinates.lng,
          })
          if (!sites) {
            throw new Error('Error retrieving stores')
          }
          const stores = mapSitesToStores(sites)

          const storesWithDistance: StoreWithDistance[] = stores.map(
            (store, index) => ({
              store,
              distance: sites[index].distanceInMeters,
              index,
            }),
          )

          const normalizedVehicleMake = vehicleMake?.trim().toLowerCase()

          const matchingStoresWithin20Miles =
            normalizedVehicleMake && under60Months
              ? storesWithDistance.filter(
                  ({ store, distance }) =>
                    !store.isNonCag &&
                    store.name
                      .replace('Š', 'S')
                      .toLowerCase()
                      .includes(normalizedVehicleMake) &&
                    distance <= TWENTY_MILES_IN_METERS,
                )
              : []

          const nearestMatchingStore = matchingStoresWithin20Miles.sort(
            (leftStore, rightStore) => leftStore.distance - rightStore.distance,
          )[0]

          setNearestMatchingStoreWithin20Miles(nearestMatchingStore)

          setStoresWithDistance(storesWithDistance)

          setCustomerCoordinates({
            latitude: customerCoordinates.lat,
            longitude: customerCoordinates.lng,
          })
        }
      } catch {
        datadogRum.addError(new Error('Cinch Servicing: Stores Error'), {
          cinchServicing: true,
        })
        setError('Stores')
      } finally {
        setLoading(false)
      }
    },
    [under60Months, vehicleMake],
  )

  return {
    storesWithDistance,
    nearestMatchingStoreWithin20Miles,
    customerCoordinates,
    loading,
    error,
  }
}
